import _ from 'lodash';
import { faBan, faLink, faQuoteLeft, faText, faMicrophone, faEye, faUser } from '@fortawesome/pro-regular-svg-icons';
import { getUserTiers } from './tier_helpers';

// Unscored lives on the Score chip row, not in REVIEW_TABS. Mixed review
// status, empty copy, mention-row filters.
export const UNSCORED_TAB = {
  key: 'unscored',
  display: 'Unscored',
  showStatus: true,
  emptyCopy: 'No unscored mentions — every active tag has a model grade.'
};

// kind 'run' tabs page over detector runs, where the mention-only filters
// (method, confidence) neither render nor apply. showStatus marks the mixed
// views — the only places review state varies per row. emptyCopy replaces the
// generic no-results message when no search is active.
export const REVIEW_TABS = [
  { key: 'pending', display: 'Needs review', isWorkQueue: true },
  {
    key: 'unmatched',
    display: 'No match',
    kind: 'run',
    isWorkQueue: true,
    emptyCopy: 'No unmatched posts — every processed mention found a product.'
  },
  { key: 'confirmed', display: 'Confirmed' },
  { key: 'overridden', display: 'Overridden' },
  { key: 'rejected', display: 'Not a product' },
  {
    key: 'completed-runs',
    display: 'Completed runs',
    kind: 'run',
    canSelect: false,
    showStatus: true,
    emptyCopy: 'No completed detector runs yet.'
  },
  { key: '', display: 'All mentions', showStatus: true }
];

export const REVIEW_STATUS_META = {
  pending: { display: 'Needs review', className: 'pending' },
  unmatched: { display: 'No match', className: 'unmatched' },
  confirmed: { display: 'Confirmed', className: 'confirmed' },
  overridden: { display: 'Overridden', className: 'overridden' },
  rejected: { display: 'Not a product', className: 'rejected' }
};

export const METHOD_META = {
  link: { display: 'Link match', icon: faLink },
  caption: { display: 'Caption text', icon: faQuoteLeft },
  ocr: { display: 'OCR on-screen', icon: faText },
  transcript: { display: 'Transcript', icon: faMicrophone },
  text: { display: 'Text match', icon: faQuoteLeft },
  visual: { display: 'Visual match', icon: faEye },
  admin: { display: 'Admin tag', icon: faUser },
  none: { display: 'No match', icon: faBan }
};

export const CONFIDENCE_BANDS = [
  { key: 'high', display: 'High', detail: '85–100%' },
  { key: 'medium', display: 'Medium', detail: '60–84%' },
  { key: 'low', display: 'Low', detail: 'under 60%' }
];

export const PLATFORMS = [
  { key: 'instagram', display: 'Instagram' },
  { key: 'tiktok', display: 'TikTok' },
  { key: 'youtube', display: 'YouTube' }
];

// Icon-first for the tier filter dropdown; unlike tier_helpers' getTierDisplay,
// the label helper returns null so rows without a tier render nothing.
export const CREATOR_TIERS = _.orderBy(getUserTiers(), 'tier', 'asc').map(({ tier, label }) => ({ key: tier, display: label }));

export const getCreatorTierDisplay = tier => CREATOR_TIERS.find(entry => entry.key === Number(tier))?.display || null;

export const ADMIN_QUALITY_GRADES = [
  { value: 1, label: '1', title: 'No' },
  { value: 2, label: '2', title: 'Okay' },
  { value: 3, label: '3', title: 'Good' }
];

export const getAdminQualityGrade = score => ADMIN_QUALITY_GRADES.find(entry => entry.value === Number(score)) || null;

export const getEffectiveQualityScore = row => row?.adminQualityScore ?? row?.qualityScore ?? null;

export const isModelOnlyQualityScore = row => row?.adminQualityScore == null && getEffectiveQualityScore(row) != null;

export const getQualityScoreBadge = row => {
  const grade = getAdminQualityGrade(getEffectiveQualityScore(row));
  if (!grade) return null;
  const isModel = isModelOnlyQualityScore(row);
  return { ...grade, isModel, title: isModel ? `${grade.title} · Model` : grade.title };
};

export const OVERRIDE_REASONS = [
  { value: 'wrong-product-right-brand', display: 'Wrong product — right brand' },
  { value: 'wrong-brand', display: 'Wrong brand entirely' },
  { value: 'dupe-lookalike', display: 'Dupe / similar look-alike' },
  { value: 'not-in-catalog', display: 'Product not in catalog' },
  { value: 'not-a-product', display: 'Not a product mention' },
  { value: 'background-item', display: 'Detector matched background item' }
];

// The category filter offers each visible industry as a whole plus its
// visible departments; option values encode which taxonomy level was picked.
export const getCategoryFilterGroups = (industries, departments) =>
  industries
    .filter(industry => !industry.isHidden)
    .map(industry => ({
      label: industry.name,
      options: [
        { value: `industry-${industry.id}`, display: `All ${industry.name}` },
        ..._.sortBy(departments.filter(department => department.Industry_id === industry.id && !department.isHidden), 'name').map(department => ({
          value: `department-${department.id}`,
          display: department.name
        }))
      ]
    }));

export const getCategoryFilterParams = category => {
  const [, kind, id] = String(category || '').match(/^(industry|department)-(\d+)$/) || [];
  if (!kind) return {};
  return kind === 'industry' ? { Industry_id: Number(id) } : { Department_id: Number(id) };
};

const PLATFORM_DISPLAY = Object.fromEntries(PLATFORMS.map(({ key, display }) => [key, display]));
const MISS_SNIPPET_CHARS = 160;

export const bandOf = confidence => {
  if (confidence == null || !Number.isFinite(Number(confidence))) return null;
  return confidence >= 0.85 ? 'high' : confidence >= 0.6 ? 'medium' : 'low';
};

export const getPercentDisplay = confidence => {
  const band = bandOf(confidence);
  return band ? `${Math.round(Number(confidence) * 100)}%` : '—';
};

export const getPlatformDisplay = platform => PLATFORM_DISPLAY[platform] || platform;

export const getOriginalPostLabel = platform =>
  platform === 'youtube' ? 'Watch on YouTube' : 'Open original post';

export const isWatchablePost = row =>
  Boolean(row?.postVideo || (row?.platform === 'youtube' && row?.postUrl));

export const getYoutubeEmbedUrl = url => {
  const id = getYoutubeVideoId(url);
  return id ? `https://www.youtube.com/embed/${id}` : null;
};

const getYoutubeVideoId = url => {
  if (!url) return null;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.replace(/^(www|m)\./, '').toLowerCase();
  const parts = parsed.pathname.split('/').filter(Boolean);
  if (host === 'youtu.be') return youtubeVideoId(parts[0]);
  if (host !== 'youtube.com') return null;
  if (parts[0] === 'watch') return youtubeVideoId(parsed.searchParams.get('v'));
  if (parts[0] === 'shorts' || parts[0] === 'live' || parts[0] === 'embed') return youtubeVideoId(parts[1]);
  return null;
};

const youtubeVideoId = id => (typeof id === 'string' && /^[A-Za-z0-9_-]{6,32}$/.test(id) ? id : null);

export const isRunRow = row => row.kind === 'run';

export const isUnmatchedRow = row =>
  isRunRow(row) && !Number(row.matchCount) && row.reviewStatus === 'unmatched';

export const getReviewMeta = row => {
  if (isRunRow(row) && !Number(row.matchCount) && row.reviewStatus === 'rejected') {
    return { display: 'Correct — no product', className: 'rejected' };
  }
  return REVIEW_STATUS_META[row.reviewStatus] || REVIEW_STATUS_META.pending;
};

// An override supersedes the detector's pick for display; the original stays
// on the row as the retraining datum.
export const getDisplayProduct = row => ({
  title: row.OverrideProduct_id ? row.overrideProductTitle : row.productTitle || 'No product found',
  image: row.OverrideProduct_id ? row.overrideProductImage : row.productImage,
  productId: row.OverrideProduct_id || row.Product_id,
  brandName: row.brandName,
  isOverridden: Boolean(row.OverrideProduct_id)
});

export const getMissEvidence = record => {
  if (!record) return [];
  const extracted = (record.product_references || [])
    .map(reference => [reference.brand, reference.product_name].filter(Boolean).join(' '))
    .filter(Boolean);
  return [
    { key: 'OCR', value: snippet(record.ocr_text) || 'none' },
    { key: 'Transcript', value: snippet(record.transcript) || 'none' },
    { key: 'Extracted', value: extracted.length ? extracted.join(', ') : 'none' },
    (record.product_cohort?.excluded || []).length > 0 && {
      key: 'Excluded matches',
      value: record.product_cohort.excluded.map(entry =>
        `Product #${entry.product_id} · ${entry.reason === 'brand_not_in_active_cohort'
          ? 'Brand is outside the approved list or has no active subscription' : _.startCase(entry.reason)}`
      ).join('; ')
    },
    record.extraction_error && { key: 'Extract error', value: record.extraction_error },
    (record.errors || []).length && { key: 'Source errors', value: record.errors.join('; ') }
  ].filter(Boolean);
};

export const getVisualEvidence = (record, productId) => {
  if (!record) return [];
  const gate = record.quality_gate;
  const visual = record.identified?.find(entry => entry.product_id === productId)?.visual;
  const results = [
    ...(record.visual?.verified || []).map(entry => ({ ...entry.contrastive, productIds: [entry.product_id] })),
    ...(record.visual?.resolved || []).map(entry => ({
      ...entry.gemini,
      outcome: entry.outcome || entry.gemini?.verdict,
      productIds: [...(entry.product_ids || []), ...(entry.gemini?.visible_members || []).map(member => member.product_id)]
    }))
  ].filter(result => !productId || result.productIds.includes(productId));
  const timestamp = visual?.bestFrameTimestampSeconds;
  const bounds = (visual?.instances || [])
    .filter(instance => instance.matched_product_id === productId && isProductBox(instance.box_2d))
    .map(instance => {
      const [top, left, bottom, right] = instance.box_2d.map(value => Math.round(value / 10));
      return `Frame ${instance.best_frame_index}: top ${top}%, left ${left}%, bottom ${bottom}%, right ${right}%`;
    });
  return [
    gate && {
      key: 'Quality gate',
      value: [
        gate.passed === true ? 'Passed' : gate.passed === false ? 'Excluded' : 'Not evaluated',
        gate.reason,
        ...(gate.reasons || []).map(getVisualDecisionDisplay)
      ].filter(Boolean).join(' · ')
    },
    ...results.map((result, index) => ({
      key: results.length > 1 ? `Visual check ${index + 1}` : 'Visual check',
      value: [
        !productId && result.productIds.length && `Product ${[...new Set(result.productIds)].map(id => `#${id}`).join(', ')}`,
        getVisualDecisionDisplay(result.outcome || result.verdict || 'Not evaluated'),
        result.reasoning || result.error
      ].filter(Boolean).join(' · ')
    })),
    Number.isFinite(timestamp) && timestamp >= 0 && { key: 'Best frame', value: `${Math.round(timestamp * 10) / 10}s · sampled video frame` },
    bounds.length > 0 && { key: 'Product bounds', value: bounds.join('; ') }
  ].filter(Boolean);
};

const VISUAL_DECISION_DISPLAY = {
  no_media: 'No image or video available',
  unsupported_platform: 'Platform is not supported',
  other_app_ui: 'Another app or screen is shown',
  no_physical_product: "No physical product in the creator's footage",
  confirmed: 'Product confirmed',
  sibling_visible_ours_absent: 'A similar product is visible; this product is absent',
  member_identified: 'Product identified among similar candidates',
  members_identified: 'Products identified among similar candidates',
  neighbor_identified: 'A related catalog product is visible',
  family_visible_member_unclear: 'Similar products are visible; the exact product is uncertain',
  not_visible: 'Product is not visible',
  unclear: 'Image quality is insufficient to identify the product',
  skipped: 'Comparison could not be completed',
  error: 'Comparison failed'
};

const getVisualDecisionDisplay = decision => VISUAL_DECISION_DISPLAY[decision] || _.startCase(decision);

const isProductBox = box => Array.isArray(box) && box.length === 4 &&
  box.every(value => Number.isFinite(value) && value >= 0 && value <= 1000) && box[0] < box[2] && box[1] < box[3];

const snippet = text => {
  if (!text) return null;
  const trimmed = String(text).replace(/\s+/g, ' ').trim();
  if (!trimmed) return null;
  return trimmed.length > MISS_SNIPPET_CHARS ? `${trimmed.slice(0, MISS_SNIPPET_CHARS)}…` : trimmed;
};
